import { Component, computed, inject } from '@angular/core';
import { LayoutService } from '../../../core/services/layout.service';
import { AppRoutes } from 'src/app/core/models/routes.model';
import { Router } from '@angular/router';
import { TOPBAR_CONFIG } from './config/topbar.config';
import { TOPBAR_DATA } from './data/topbar.data';
import { TopbarConfig, TopbarData } from './models/topbar.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { AppContextService } from 'src/app/core/services/app-context.service';
import { CartService } from 'src/app/core/services/ecommerce/cart.service';
import { EcommerceAuthService } from 'src/app/core/services/ecommerce/ecommerce-auth.service';
import { ActivePlansService } from 'src/app/core/services/ecommerce/active-plans.service';
import { ActivePlanModel } from 'src/app/core/models/ecommerce/license.model';
import { managementDashboardUrl } from 'src/app/core/config/management.config';
import { AvatarInfoData } from 'src/app/shared/models/ui-avatar.model';

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    standalone: false
})
export class TopbarComponent {
  router = inject(Router);
  layoutService = inject(LayoutService);
  authService = inject(AuthService);
  appContextService = inject(AppContextService);
  cartService = inject(CartService);
  private readonly ecommerceAuthService = inject(EcommerceAuthService);
  private readonly activePlansService = inject(ActivePlansService);
  config: TopbarConfig = TOPBAR_CONFIG;
  data: TopbarData = TOPBAR_DATA;

  readonly cartItemsCount = computed(() => this.cartService.cart()?.lines.length ?? 0);

  /** Il numero sul badge è grafico: il nome del comando lo deve dire a parole. */
  readonly cartLabel = computed(() => {
    const count = this.cartItemsCount();
    if (!count) {
      return 'Carrello, vuoto';
    }
    return `Carrello, ${count} ${count === 1 ? 'articolo' : 'articoli'}`;
  });

  /** Piani attivi ricavati dagli ordini confermati (vedi ActivePlansService). */
  readonly activePlans = this.activePlansService.plans;
  readonly isLoadingPlans = this.activePlansService.isLoading;
  readonly plansError = this.activePlansService.error;

  /**
   * L'app è pubblica: senza sessione Keycloak il topbar mostra "Accedi" al
   * posto della scheda profilo. Si basa sul signal `user`, valorizzato dagli
   * eventi Keycloak, così la UI reagisce a login e logout.
   */
  readonly isAuthenticated = computed(() => this.authService.user() !== null);

  get compactUser(): AvatarInfoData {
    const user = this.authService.user();
    return {
      name: user?.firstName || user?.fullName || user?.username || 'Utente',
      surname: user?.lastName || '',
      username: undefined,
      role: user?.email || 'Utente autenticato',
      avatar: undefined,
    };
  }

  get profileCardUser(): AvatarInfoData {
    const user = this.authService.user();

    return {
      name: user?.firstName || user?.fullName || user?.username || 'Utente',
      surname: user?.lastName || '',
      username: user?.username,
      role: user?.email || 'Utente autenticato',
      avatar: undefined,
    };
  }

  /** "Gestisci": apre la dashboard dell'app gestionale nell'environment corrente. */
  manageActivePlan(plan: ActivePlanModel): void {
    window.open(managementDashboardUrl(), '_blank', 'noopener');
  }

  reloadActivePlans(): void {
    void this.activePlansService.reload();
  }

  trackActivePlan(_index: number, plan: ActivePlanModel): string {
    return plan.licenseId;
  }

  navigateToCart(): void {
    this.router.navigateByUrl(this.appContextService.buildAppScopedUrl(AppRoutes.CARRELLO));
  }

  /** Login avviato dall'utente: al ritorno si atterra sulla pagina corrente. */
  async login(): Promise<void> {
    await this.authService.login(this.router.url);
  }

  /** Chiude anche l'eventuale sessione ecommerce legacy prima del logout Keycloak. */
  async logout(): Promise<void> {
    await this.ecommerceAuthService.logout();
  }
}
